import type { Bundle } from '../validate';
import type { ArcDef, IssueStoryDef, LocationRuleDef } from './types';

/*
 * Display helpers for the Story tab's cover preview. `wherePreview` ports shared-cs `LocationRules.WhereText` (S4-SRV), the cover's
 * "Where" strip, without a bound building (the admin has no player).
 */

const humanize = (id: string | null | undefined) => (id ?? '').replace(/_/g, ' ').trim();

/** `RaidPlanner.SmallMaxM2`: a footprint at or above this reads as "big". */
const SMALL_MAX_M2 = 400;

/** `LocationRules.Place`: "Any hospital or clinic", "Any big food store". */
function place(l: LocationRuleDef, bundle?: Bundle): string {
  const names: string[] = [];
  for (const k of l.kinds ?? []) names.push(humanize(k));
  for (const c of l.categories ?? []) {
    const name = bundle?.categories?.find((x) => x != null && x.id === c)?.name;
    names.push(name ? name.toLowerCase() : humanize(c));
  }
  const list = [...new Set(names.filter((n) => n.length > 0))];
  if (list.length === 0) return 'Anywhere';
  const text =
    list.length === 1 ? list[0]! : `${list.slice(0, -1).join(', ')} or ${list[list.length - 1]}`;
  const big = (l.minRooms ?? 0) >= 6 || (l.minAreaM2 ?? 0) >= SMALL_MAX_M2 ? 'big ' : '';
  return `Any ${big}${text}`;
}

/** `LocationRules.WhereText` with no bound building: "Any hospital or clinic", "The same place as #1", "Anywhere". */
export function wherePreview(loc: LocationRuleDef | undefined, bundle?: Bundle): string {
  const l = loc ?? {};
  switch (l.kind ?? 'place') {
    case 'anywhere':
      return 'Anywhere';
    case 'sameAs': {
      const m = bundle?.missions?.find((x) => x?.id === l.issue);
      return `The same place as #${m?.story?.number ?? 0}`;
    }
    case 'picked':
      return `${place(l, bundle)} (picked for you)`;
    default:
      return place(l, bundle);
  }
}

/** The arc that lists this issue, else the arc its story block names. */
export function arcOfIssue(bundle: Bundle, missionId: string, story?: IssueStoryDef | null) {
  const arcs = (bundle.arcs ?? []) as ArcDef[];
  return (
    arcs.find((a) => (a.issues ?? []).includes(missionId)) ??
    arcs.find((a) => a.id && a.id === story?.arcId)
  );
}

/** A palette hex colour from content, or Blam Red. */
export const logoColor = (c?: string) => (c && /^#[0-9a-f]{6}$/i.test(c) ? c : '#e3242b');
